import { useState } from 'react';
import type { MetaCampaign, GoogleCampaign, ServiceLabel } from '../types';
import { formatBRL, formatPercent, formatNumber } from '../lib/format';
import { SERVICE_COLORS } from '../types';

type Campaign = MetaCampaign | GoogleCampaign;

function ServiceBadge({ service }: { service: string | null }) {
  if (!service) {
    return (
      <span style={{
        padding: '2px 8px',
        borderRadius: '12px',
        fontSize: '11px',
        fontFamily: 'var(--font-mono)',
        background: 'var(--bg-tertiary)',
        color: 'var(--text-disabled)',
        border: '1px solid var(--border-subtle)',
      }}>
        —
      </span>
    );
  }
  const color = SERVICE_COLORS[service as ServiceLabel] ?? 'var(--text-secondary)';
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: '12px',
      fontSize: '11px',
      fontFamily: 'var(--font-mono)',
      background: `${color}20`,
      color,
      border: `1px solid ${color}40`,
    }}>
      {service.replace('LF | ', '')}
    </span>
  );
}

interface Props {
  campaigns: Campaign[];
  showSource?: boolean;
}

type SortKey = 'spend' | 'impressions' | 'ctr' | 'cpm' | 'results' | 'conversions';

export function CampaignTable({ campaigns, showSource = false }: Props) {
  const [sortKey, setSortKey] = useState<SortKey>('spend');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');

  function handleSort(key: SortKey) {
    if (key === sortKey) {
      setSortDir(d => d === 'desc' ? 'asc' : 'desc');
    } else {
      setSortKey(key);
      setSortDir('desc');
    }
  }

  const sorted = [...campaigns].sort((a, b) => {
    const aVal = (a as Record<string, unknown>)[sortKey] as number ?? 0;
    const bVal = (b as Record<string, unknown>)[sortKey] as number ?? 0;
    return sortDir === 'desc' ? bVal - aVal : aVal - bVal;
  });

  function th(label: string, key?: SortKey) {
    const isActive = key === sortKey;
    return (
      <th
        key={label}
        onClick={key ? () => handleSort(key) : undefined}
        style={{
          padding: '10px 14px',
          textAlign: key ? 'right' : 'left',
          fontSize: '11px',
          fontWeight: 600,
          letterSpacing: '0.06em',
          textTransform: 'uppercase' as const,
          color: isActive ? 'var(--accent)' : 'var(--text-tertiary)',
          fontFamily: 'var(--font-sans)',
          cursor: key ? 'pointer' : 'default',
          whiteSpace: 'nowrap',
          userSelect: 'none',
        }}
      >
        {label} {isActive ? (sortDir === 'desc' ? '↓' : '↑') : ''}
      </th>
    );
  }

  if (campaigns.length === 0) {
    return (
      <div style={{
        background: 'var(--bg-elevated)',
        border: '1px solid var(--border-subtle)',
        borderRadius: '12px',
        padding: '40px',
        textAlign: 'center',
        color: 'var(--text-tertiary)',
        fontSize: '13px',
      }}>
        Nenhuma campanha encontrada
      </div>
    );
  }

  return (
    <div style={{
      background: 'var(--bg-elevated)',
      border: '1px solid var(--border-subtle)',
      borderRadius: '12px',
      overflow: 'hidden',
    }}>
      <div style={{ overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ borderBottom: '1px solid var(--border-subtle)' }}>
              {th('Campanha')}
              {showSource && th('Fonte')}
              {th('Gasto', 'spend')}
              {th('Impressões', 'impressions')}
              {th('CTR', 'ctr')}
              {th('CPM', 'cpm')}
              {sorted[0]?.source === 'meta' ? th('Result.', 'results') : th('Convers.', 'conversions')}
              {th('Serviço')}
            </tr>
          </thead>
          <tbody>
            {sorted.map((c, i) => {
              const isMeta = c.source === 'meta';
              const meta = isMeta ? (c as MetaCampaign) : null;
              const google = !isMeta ? (c as GoogleCampaign) : null;
              return (
                <tr
                  key={`${c.campaign_id}_${i}`}
                  style={{
                    borderBottom: i < sorted.length - 1 ? '1px solid var(--border-subtle)' : 'none',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.background = 'var(--bg-tertiary)')}
                  onMouseLeave={e => (e.currentTarget.style.background = 'transparent')}
                >
                  <td style={{ padding: '10px 14px', maxWidth: '240px' }}>
                    <div style={{ fontSize: '13px', color: 'var(--text-primary)', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {c.campaign_name}
                    </div>
                    {isMeta && (
                      <div style={{ fontSize: '11px', color: 'var(--text-tertiary)', fontFamily: 'var(--font-mono)', marginTop: '2px' }}>
                        {(c as MetaCampaign).account_id}
                      </div>
                    )}
                  </td>
                  {showSource && (
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{
                        padding: '2px 8px',
                        borderRadius: '4px',
                        fontSize: '11px',
                        fontFamily: 'var(--font-mono)',
                        background: isMeta ? 'rgba(59,130,246,0.12)' : 'rgba(34,197,94,0.12)',
                        color: isMeta ? 'var(--blue)' : 'var(--green)',
                      }}>
                        {isMeta ? 'Meta' : 'Google'}
                      </span>
                    </td>
                  )}
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                    {formatBRL(c.spend)}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                    {formatNumber(c.impressions)}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                    {formatPercent(c.ctr)}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                    {formatBRL(c.cpm)}
                  </td>
                  <td style={{ padding: '10px 14px', textAlign: 'right', fontFamily: 'var(--font-mono)', fontSize: '13px' }}>
                    {meta ? formatNumber(meta.results) : formatNumber(google!.conversions)}
                  </td>
                  <td style={{ padding: '10px 14px' }}>
                    <ServiceBadge service={c.service_tag} />
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
